import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useWallet } from "@/context/WalletContext";
import { hasPhantom, hasEvm } from "@/lib/wallet";
import { Wallet, ArrowRight, Ghost, Boxes, FlaskConical } from "lucide-react";

export function ConnectMenu({ size, label = "Connect wallet", testid = "landing-connect-wallet", showArrow = false }) {
  const navigate = useNavigate();
  const { wallet, connect, connectPhantom, connectEvm, connecting } = useWallet();

  if (wallet) {
    return (
      <Button onClick={() => navigate("/app")} size={size} className="gap-2 bg-juice text-ink hover:bg-juice/90 font-semibold" data-testid={testid}>
        <Wallet size={16} /> Open app {showArrow && <ArrowRight size={16} />}
      </Button>
    );
  }

  const run = async (fn, name) => {
    try {
      await fn();
      toast.success(`Connected with ${name}`);
      navigate("/app");
    } catch (e) {
      toast.error(e?.message || `${name} connection failed`);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size={size} disabled={connecting} className="gap-2 bg-juice text-ink hover:bg-juice/90 font-semibold" data-testid={testid}>
          <Wallet size={size === "lg" ? 18 : 16} /> {connecting ? "Connecting…" : label} {showArrow && <ArrowRight size={16} />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 border-line bg-surface text-cream">
        <DropdownMenuLabel className="text-xs text-dust">Choose a wallet</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => run(connectPhantom, "Phantom")} className="cursor-pointer gap-2 focus:bg-raised" data-testid="connect-phantom">
          <Ghost size={16} className="text-juice" />
          <div className="flex-1">Phantom <span className="text-[11px] text-dust">Solana</span></div>
          {!hasPhantom() && <span className="text-[10px] text-dust/70">install</span>}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => run(connectEvm, "WalletConnect")} className="cursor-pointer gap-2 focus:bg-raised" data-testid="connect-evm">
          <Boxes size={16} className="text-pulp" />
          <div className="flex-1">WalletConnect <span className="text-[11px] text-dust">EVM</span></div>
          {!hasEvm() && <span className="text-[10px] text-dust/70">install</span>}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-line" />
        <DropdownMenuItem onClick={() => run(connect, "demo wallet")} className="cursor-pointer gap-2 focus:bg-raised" data-testid="connect-demo">
          <FlaskConical size={16} className="text-dust" />
          <div className="flex-1">Demo wallet <span className="text-[11px] text-dust">no signing</span></div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
